// pages/Brands.js - Brand-specific promotions page with brand slider
import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Building2 } from 'lucide-react';
import SinglePromotionPopup from '../components/promotion/SinglePromotionPopup';
import BrandSlider from '../components/promotion/BrandSlider';
import SearchBar from '../components/promotion/SearchBar';
import LoadingProgressBar from '../components/LoadingProgressBar';
import usePromotions from '../hooks/usePromotions';
import useBrandFilters from '../hooks/useBrandFilters';
import useInfiniteScroll from '../hooks/useInfiniteScroll';

function BrandsPage() {
  // UI State
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [showSearch, setShowSearch] = useState(false);

  // Custom hooks for data management
  const {
    promotions,
    allPromotions,
    loading,
    refreshing,
    loadingMore,
    loadingProgress,
    error,
    lastUpdated,
    hasMore,
    refreshPromotions,
    loadMore
  } = usePromotions(30);

  const {
    filters,
    filteredPromotions,
    brands,
    updateBrand,
    updateSearchQuery,
    hasActiveFilters
  } = useBrandFilters(allPromotions);

  // Extract brand logos from promotions
  const brandLogos = useMemo(() => {
    const logos = {};
    allPromotions.forEach(promotion => {
      const brandName = promotion.brandName || promotion.brand;
      if (brandName && promotion.brandLogo) {
        logos[brandName] = promotion.brandLogo;
      }
    });
    return logos;
  }, [allPromotions]);

  // Responsive columns for masonry grid
  const [columns, setColumns] = useState(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1024) return 3; // Desktop: 3 columns
      if (width >= 768) return 2;  // Tablet: 2 columns
      return 1; // Mobile: 1 column
    }
    return 3;
  });

  // Infinite scroll for pagination
  const { loadingRef } = useInfiniteScroll({
    hasMore: hasMore && !hasActiveFilters,
    loadMore,
    threshold: 300,
    enabled: !loading && !error
  });


  // Handle responsive columns
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setColumns(3); // Desktop: 3 columns
      } else if (width >= 768) {
        setColumns(2); // Tablet: 2 columns
      } else {
        setColumns(1); // Mobile: 1 column
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Search handlers
  const handleToggleSearch = () => {
    setShowSearch(!showSearch);
  };

  const handleSearchChange = (query) => {
    updateSearchQuery(query);
  };

  const handleSearchClear = () => {
    updateSearchQuery('');
    setShowSearch(false);
  };

  // Brand handlers
  const handleBrandChange = (brand) => {
    updateBrand(brand);
  };

  // Promotion handlers
  const handlePromotionClick = (promotion) => {
    setSelectedPromotion(promotion);
    // Prevent scrolling when popup is open
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setSelectedPromotion(null);
    // Re-enable scrolling
    document.body.style.overflow = 'auto';
  };

  // Get display data - Always use filtered promotions since we always have a brand selected
  const displayPromotions = filteredPromotions;
  const totalCount = allPromotions.length;

  // Debug logging
  useEffect(() => {
    console.log('Debug - Current brand filter:', filters.brand);
    console.log('Debug - All promotions count:', allPromotions.length);
    console.log('Debug - Filtered promotions count:', filteredPromotions.length);
    console.log('Debug - Display promotions count:', displayPromotions.length);
    console.log('Debug - Available brands:', brands);
    
    if (displayPromotions.length > 0) {
      console.log('First promotion data:', displayPromotions[0]);
    }
  }, [displayPromotions, loading, error, filters.brand, allPromotions.length, filteredPromotions.length, brands]);

  // Final display promotions (filtering is already handled by useBrandFilters)
  const finalDisplayPromotions = displayPromotions;

  return (
    <>
      <Helmet>
        <title>Promosi Casino Online by Brands - Cek Bonus</title>
        <meta
          name="description"
          content="Temukan promosi casino online terbaik berdasarkan brand favorit Anda. Bonus eksklusif dari Pragmatic Play, Evolution Gaming, dan brand terpercaya lainnya di Indonesia."
        />
        <meta
          name="keywords"
          content="promosi casino brand, bonus pragmatic play, evolution gaming bonus, casino online indonesia, promosi eksklusif"
        />
        <link rel="canonical" href="https://cekbonus.com/bybrands" />

        {/* Open Graph */}
        <meta property="og:title" content="Promosi Casino Online by Brands - Cek Bonus" />
        <meta property="og:description" content="Temukan promosi casino online terbaik berdasarkan brand favorit Anda. Bonus eksklusif dari brand terpercaya." />
        <meta property="og:url" content="https://cekbonus.com/bybrands" />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:title" content="Promosi Casino Online by Brands - Cek Bonus" />
        <meta name="twitter:description" content="Temukan promosi casino online terbaik berdasarkan brand favorit Anda." />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Promosi Casino Online by Brands",
            "description": "Halaman promosi casino online berdasarkan brand game provider",
            "url": "https://cekbonus.com/bybrands",
            "mainEntity": {
              "@type": "ItemList",
              "name": "Brand Casino Promotions",
              "description": "Daftar promosi casino dari berbagai brand provider"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">

      {/* Loading Progress */}
      {loading && <LoadingProgressBar progress={loadingProgress} />}

      {/* Brand Slider */}
      <BrandSlider
        brands={brands}
        selectedBrand={filters.brand}
        onBrandChange={handleBrandChange}
        onToggleSearch={handleToggleSearch}
        showRefresh={false}
        stickyOffset={56}
        brandLogos={brandLogos}
      />

      {/* Search Bar */}
      {showSearch && (
        <div className="px-4 mt-3">
          <SearchBar
            value={filters.searchQuery}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder="Search brand promotions..."
            autoFocus={true}
            className="animate-fadeIn"
          />
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-blue-700 font-medium">Active filters:</span>
              {filters.brand && (
                <span className="bg-blue-600 text-white px-2 py-1 rounded-full text-xs">
                  Brand: {filters.brand}
                </span>
              )}
              {filters.searchQuery && (
                <span className="bg-green-600 text-white px-2 py-1 rounded-full text-xs">
                  Search: "{filters.searchQuery}"
                </span>
              )}
              <button
                onClick={() => {
                  updateSearchQuery('');
                }}
                className="text-blue-600 hover:text-blue-800 underline text-xs"
              >
                Clear search
              </button>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center text-red-700">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Error loading promotions</span>
            </div>
            <p className="mt-1 text-red-600 text-sm">{error.message}</p>
            <button
              onClick={refreshPromotions}
              className="mt-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && finalDisplayPromotions.length === 0 && (
          <div className="text-center py-12">
            <Building2 size={64} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {hasActiveFilters ? 'No promotions found' : 'No brand promotions available'}
            </h3>
            <p className="text-gray-500 mb-4">
              {hasActiveFilters
                ? 'Try adjusting your search criteria or selected brand.'
                : 'Check back later for new brand promotions.'
              }
            </p>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  updateSearchQuery('');
                }}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* Promotions Masonry Grid */}
        {!loading && !error && finalDisplayPromotions.length > 0 && (
          <>
            <div className="flex flex-row -mx-2">
              {(() => {
                // Create columns for masonry layout
                const promotionColumns = [];
                for (let i = 0; i < columns; i++) {
                  promotionColumns.push([]);
                }
                
                // Distribute promotions across columns
                finalDisplayPromotions.forEach((promo, index) => {
                  const columnIndex = index % columns;
                  promotionColumns[columnIndex].push(promo);
                });
                
                return promotionColumns.map((columnPromotions, columnIndex) => (
                  <div key={columnIndex} className={`px-2 ${columns === 1 ? 'w-full' : columns === 2 ? 'w-1/2' : 'w-1/3'}`}>
                    {columnPromotions.map((promotion) => (
                      <div
                        key={promotion.id}
                        className="mb-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow cursor-pointer overflow-hidden"
                        onClick={() => handlePromotionClick(promotion)}
                      >
                  <div className="relative">
                    {promotion.image ? (
                      <div className="relative">
                        <img
                          src={promotion.image}
                          alt={promotion.title || "Promotion"}
                          className="w-full h-auto object-cover"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = '/img/placeholder-promotion.png';
                          }}
                        />
                        {/* Gradient overlay and title - only show if title exists */}
                        {promotion.title && (
                          <>
                            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent"></div>
                            <h3 className="absolute bottom-2 left-2 right-2 text-white font-semibold text-sm line-clamp-2">
                              {promotion.title}
                            </h3>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="relative bg-purple-50 h-48 flex items-center justify-center">
                        <Building2 className="h-12 w-12 text-purple-300" />
                        {/* Gradient overlay and title - only show if title exists */}
                        {promotion.title && (
                          <>
                            <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black to-transparent"></div>
                            <h3 className="absolute bottom-2 left-2 right-2 text-white font-semibold text-sm line-clamp-2">
                              {promotion.title}
                            </h3>
                          </>
                        )}
                      </div>
                    )}
                    
                    {/* Brand Badge - Top left */}
                    {(promotion.brandName || promotion.brand) && (
                      <div className="absolute top-2 left-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        {promotion.brandName || promotion.brand}
                      </div>
                    )}
                    
                    {/* Category Badge - Top right */}
                    {promotion.category && (
                      <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                        {promotion.category}
                      </div>
                    )}
                  </div>
                  {/* Optional content section for additional info */}
                  {(promotion.description || promotion.discountValue || promotion.validUntil) && (
                    <div className="p-3">
                      {promotion.description && (
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                          {promotion.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        {promotion.discountValue && (
                          <span className="text-purple-600 font-bold text-sm">
                            {promotion.discountValue}
                          </span>
                        )}
                        {promotion.validUntil && (
                          <span className="text-gray-500 text-xs">
                            {promotion.validUntil}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>

            {/* Load More / Loading */}
            {(hasMore && !hasActiveFilters) && (
              <div className="flex justify-center mt-8">
                {loadingMore ? (
                  <div className="flex items-center text-purple-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600 mr-2"></div>
                    Loading more promotions...
                  </div>
                ) : (
                  <div ref={loadingRef} className="h-4" />
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Promotion Popup */}
      {selectedPromotion && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          style={{ animation: 'fadeIn 0.3s ease-out' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closePopup();
            }
          }}
        >
          <SinglePromotionPopup
            promotion={selectedPromotion}
            onClose={closePopup}
          />
        </div>
      )}

      {/* Global Styles */}
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
      </div>
    </>
  );
}

export default BrandsPage;